import React, { useState, useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Loader2, Info } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Badge } from "src/components/ui/badge";
import { Alert, AlertDescription } from "src/components/ui/alert";

// Updated categories and colors to match AcceptanceDistribution
const SCORE_CATEGORIES = {
  absoluteAcceptance: {
    name: "Absolútny súhlas",
    range: [5, 5],
    color: "#22c55e", // Green
  },
  acceptance: {
    name: "Súhlas s výhradami",
    range: [3, 4],
    color: "#eab308", // Yellow
  },
  disagreement: {
    name: "Nesúhlas",
    range: [1, 2],
    color: "#ef4444", // Red
  },
  absoluteDisagreement: {
    name: "Absolútny nesúhlas",
    range: [0, 0],
    color: "#1f2937", // Dark gray (almost black)
  },
};

// Convert to format used by the pie chart
const COLORS = {
  "Absolútny súhlas": SCORE_CATEGORIES.absoluteAcceptance.color,
  "Súhlas s výhradami": SCORE_CATEGORIES.acceptance.color,
  Nesúhlas: SCORE_CATEGORIES.disagreement.color,
  "Absolútny nesúhlas": SCORE_CATEGORIES.absoluteDisagreement.color,
};

const SelfSelectionDistribution = ({
  evaluationData,
  loading = false,
  error = null,
  hideHeader = false,
}) => {
  // Process data from the parent component
  const { chartData, metrics, selfSelectionDetails } = useMemo(() => {
    // Initialize with default empty values
    const defaultResult = {
      chartData: [],
      metrics: {
        selfSelectionCoefficient: 0,
        totalEvaluations: 0,
      },
      selfSelectionDetails: [],
    };

    // Return early if no data available
    if (
      !evaluationData ||
      !evaluationData.solutions ||
      evaluationData.solutions.length === 0
    ) {
      return defaultResult;
    }

    // Initialize counts for each category
    const counts = {
      "Absolútny súhlas": 0,
      "Súhlas s výhradami": 0,
      Nesúhlas: 0,
      "Absolútny nesúhlas": 0,
    };

    // Initialize user lists for each category
    const categoryUsers = {
      "Absolútny súhlas": [],
      "Súhlas s výhradami": [],
      Nesúhlas: [],
      "Absolútny nesúhlas": [],
    };

    const details = [];

    // Process each solution and look for self-evaluations
    evaluationData.solutions.forEach((solution) => {
      if (!solution.user_id) return;

      const selfEvaluation = solution.evaluations.find(
        (e) => e.evaluator_id === solution.user_id
      );

      if (!selfEvaluation) return;

      const userName = solution.users
        ? `${solution.users.first_name} ${solution.users.last_name}`
        : "Unknown";

      const score = selfEvaluation.score;

      // Add to details list
      details.push({
        name: userName,
        score: score,
      });

      // Categorize based on score ranges
      if (score === 5) {
        counts["Absolútny súhlas"]++;
        categoryUsers["Absolútny súhlas"].push({
          name: userName,
          score: score,
        });
      } else if (score >= 3 && score <= 4) {
        counts["Súhlas s výhradami"]++;
        categoryUsers["Súhlas s výhradami"].push({
          name: userName,
          score: score,
        });
      } else if (score >= 1 && score <= 2) {
        counts["Nesúhlas"]++;
        categoryUsers["Nesúhlas"].push({
          name: userName,
          score: score,
        });
      } else if (score === 0) {
        counts["Absolútny nesúhlas"]++;
        categoryUsers["Absolútny nesúhlas"].push({
          name: userName,
          score: score,
        });
      }
    });

    // Calculate total users and coefficient
    const totalUsers = Object.values(counts).reduce((sum, val) => sum + val, 0);

    const selfSelectionCoefficient =
      totalUsers > 0 ? counts["Absolútny súhlas"] / totalUsers : 0;

    // Format metrics
    const calculatedMetrics = {
      selfSelectionCoefficient,
      totalEvaluations: totalUsers,
    };

    // Format data for the chart
    const formattedChartData = Object.entries(counts).map(([name, value]) => ({
      name,
      value,
      users: categoryUsers[name],
    }));

    // Format details for display
    const formattedDetails = details.map((detail) => ({
      ...detail,
      category:
        Object.entries(categoryUsers).find(([, users]) =>
          users.some((u) => u.name === detail.name)
        )?.[0] || "Unknown",
    }));

    return {
      chartData: formattedChartData,
      metrics: calculatedMetrics,
      selfSelectionDetails: formattedDetails,
    };
  }, [evaluationData]);

  const renderColorfulLegendText = (value) => {
    return <span className="text-sm font-medium text-gray-700">{value}</span>;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, value, users } = payload[0].payload;
      return (
        <div className="bg-white p-4 border rounded-lg shadow-lg max-w-xs">
          <div className="font-medium text-lg mb-2 flex items-center">
            <div
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: COLORS[name] }}
            />
            {name}
          </div>
          <div className="text-sm text-gray-600 mb-2">
            Počet používateľov: {value}
            <br />
            Podiel:{" "}
            {metrics.totalEvaluations > 0
              ? ((value / metrics.totalEvaluations) * 100).toFixed(1)
              : 0}
            %
          </div>
          {users && users.length > 0 && (
            <div className="mt-2">
              <div className="font-medium mb-1">Používatelia:</div>
              <div className="max-h-40 overflow-y-auto">
                <ul className="list-disc pl-4 space-y-1">
                  {users.map((user, index) => (
                    <li key={index} className="text-gray-700">
                      {user.name} (skóre: {user.score})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Handle the case when no data is available
  const noData = !chartData.length || metrics.totalEvaluations === 0;

  const emptyStateContent = (
    <div className="flex justify-center items-center h-64 bg-gray-50 rounded-lg">
      <p className="text-gray-500">
        Žiadne dáta o sebahodnotení nie sú dostupné
      </p>
    </div>
  );

  if (noData) {
    // If hideHeader is true, just return the empty state without card
    if (hideHeader) {
      return emptyStateContent;
    }

    // Otherwise return empty state with card structure
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center space-x-2">
            <CardTitle>Sebahodnotenie</CardTitle>
            <HoverCard>
              <HoverCardTrigger asChild>
                <Info className="h-4 w-4 text-gray-500 cursor-help" />
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <div className="space-y-2">
                  <h4 className="font-semibold">Ako sa počítajú kategórie?</h4>
                  {/* Category explanations */}
                </div>
              </HoverCardContent>
            </HoverCard>
          </div>
        </CardHeader>
        <CardContent>{emptyStateContent}</CardContent>
      </Card>
    );
  }

  const content = (
    <div className="h-[400px]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart className="mt-10">
          <Pie
            data={chartData}
            cx="50%"
            cy="45%"
            innerRadius={60}
            outerRadius={140}
            fill="#8884d8"
            paddingAngle={5}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[entry.name]}
                className="hover:opacity-90 transition-opacity"
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={renderColorfulLegendText}
            iconType="circle"
            iconSize={10}
            wrapperStyle={{
              paddingTop: "0px",
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );

  if (hideHeader) {
    return content;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center space-x-2">
          <CardTitle>Sebahodnotenie</CardTitle>
          <HoverCard>
            <HoverCardTrigger asChild>
              <Info className="h-4 w-4 text-gray-500 cursor-help" />
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              <div className="space-y-2">
                <h4 className="font-semibold">Ako sa počítajú kategórie?</h4>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor:
                        SCORE_CATEGORIES.absoluteAcceptance.color,
                    }}
                  />
                  <p className="text-sm text-gray-500">
                    <strong>Absolútny súhlas</strong>: 5 bodov
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor: SCORE_CATEGORIES.acceptance.color,
                    }}
                  />
                  <p className="text-sm text-gray-500">
                    <strong>Súhlas s výhradami</strong>: 3-4 body
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor: SCORE_CATEGORIES.disagreement.color,
                    }}
                  />
                  <p className="text-sm text-gray-500">
                    <strong>Nesúhlas</strong>: 1-2 body
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor:
                        SCORE_CATEGORIES.absoluteDisagreement.color,
                    }}
                  />
                  <p className="text-sm text-gray-500">
                    <strong>Absolútny nesúhlas</strong>: 0 bodov
                  </p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>
        <div className="flex-col items-center ">
          <Badge
            variant="secondary"
            className={`${
              metrics.selfSelectionCoefficient >= 0.7
                ? "bg-green-50 text-green-700"
                : metrics.selfSelectionCoefficient >= 0.5
                ? "bg-yellow-50 text-yellow-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            {(metrics.selfSelectionCoefficient * 100).toFixed(1)}% samo-výber
          </Badge>
        </div>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
};

export default SelfSelectionDistribution;
